import React, { useState } from 'react';
import Reveal from '../components/Reveal/Reveal';
import {
  IconPhone,
  IconMail,
  IconLocation,
  IconClock,
  IconArrowRight,
  IconCheckCircle,
} from '../components/Icons/Icons';
import '../assets/css/contact-page.css';

const SERVICES = [
  'Ofgem Compliance',
  'Green Gas Certificate Scheme (GGCS)',
  'RTFO Compliance',
  'Nabisy Transfers',
  'SECR Reporting',
  'Sustainability & Net Zero',
  'Something else',
];

const DETAILS = [
  {
    icon: IconPhone,
    label: 'Call us',
    lines: [
      { text: '01984 624989', href: 'tel:01984624989' },
      { text: '07530 401925', href: 'tel:07530401925' },
    ],
  },
  {
    icon: IconMail,
    label: 'Email us',
    lines: [
      { text: 'eviegarner@sustainabilityadvisors.co.uk', href: 'mailto:eviegarner@sustainabilityadvisors.co.uk' },
    ],
  },
  {
    icon: IconLocation,
    label: 'Visit us',
    lines: [{ text: 'Higher Ford, Wiveliscombe, Taunton, Somerset, TA4 2RL' }],
  },
  {
    icon: IconClock,
    label: 'Office hours',
    lines: [{ text: 'Monday to Friday, 9:00 to 17:30' }],
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ContactUs() {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!form.email.trim()) next.email = 'Please enter your email.';
    else if (!EMAIL_RE.test(form.email)) next.email = 'Please enter a valid email address.';
    if (!form.service) next.service = 'Please let us know how we can help.';
    if (!form.consent) next.consent = 'Please accept the privacy notice to continue.';
    return next;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    // TODO: wire to a form back-end (Formspree / Netlify Forms / email API).
    setSubmitted(true);
  };

  return (
    <main className="contact-page">
      {/* ---------- Hero ---------- */}
      <section className="cu-hero">
        <div className="cu-hero-backdrop" aria-hidden="true">
          <span className="cu-glow cu-glow-teal" />
          <span className="cu-glow cu-glow-green" />
        </div>

        <div className="container cu-hero-inner">
          <Reveal direction="up">
            <span className="section-label">Contact Us</span>
          </Reveal>
          <Reveal direction="up" delay={90}>
            <h1 className="cu-hero-title">
              Let us talk about your <span className="cu-accent">compliance</span>
            </h1>
          </Reveal>
          <Reveal direction="up" delay={170}>
            <p className="cu-hero-lead">
              Tell us about your business and where you are on your compliance journey. Whether you
              need certification support, regulatory reporting or carbon reporting guidance, our team
              is ready to help.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- Main ---------- */}
      <section className="section cu-main">
        <div className="container cu-grid">
          {/* Contact details + map */}
          <Reveal direction="left">
            <aside className="cu-info">
              <h2 className="cu-info-title">Get in touch</h2>
              <p className="cu-info-text">
                Fill in the form and our team will get back to you within one working day.
              </p>

              <ul className="cu-detail-grid">
                {DETAILS.map((detail) => {
                  const Icon = detail.icon;
                  return (
                    <li className="cu-detail-tile" key={detail.label}>
                      <span className="cu-detail-icon">
                        <Icon width={17} height={17} />
                      </span>
                      <span className="cu-detail-label">{detail.label}</span>
                      <div className="cu-detail-body">
                        {detail.lines.map((line) =>
                          line.href ? (
                            <a key={line.text} href={line.href} className="cu-detail-value cu-detail-link">
                              {line.text}
                            </a>
                          ) : (
                            <span key={line.text} className="cu-detail-value">
                              {line.text}
                            </span>
                          )
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="cu-map-card" aria-label="Our location">
                <iframe
                  title="Sustainability Advisory Ltd location in Wiveliscombe, Somerset"
                  src="https://maps.google.com/maps?q=Wiveliscombe%2C%20Taunton%2C%20Somerset%2C%20TA4%202RL&t=&z=12&ie=UTF8&iwloc=&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <a
                  className="cu-map-link"
                  href="https://www.google.com/maps/search/?api=1&query=Higher+Ford%2C+Wiveliscombe%2C+Taunton%2C+Somerset%2C+TA4+2RL"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconLocation width={14} height={14} /> Get directions
                </a>
              </div>
            </aside>
          </Reveal>

          {/* Form */}
          <Reveal direction="right" delay={120}>
            <div className="cu-form-card">
              {submitted ? (
                <div className="cu-success" role="status">
                  <span className="cu-success-icon">
                    <IconCheckCircle width={34} height={34} />
                  </span>
                  <h2 className="cu-success-title">Thank you, your message has been received</h2>
                  <p className="cu-success-text">
                    Thanks for getting in touch, {form.name.split(' ')[0] || 'there'}. We have
                    received your enquiry and will get back to you within one working day.
                  </p>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', company: '', email: '', phone: '', service: '', message: '', consent: false });
                    }}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="cu-form" onSubmit={handleSubmit} noValidate>
                  <h2 className="cu-form-title">Send us a message</h2>

                  <div className="cu-field-row">
                    <div className="cu-field">
                      <label htmlFor="name">Full name <span className="cu-req">*</span></label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        className={errors.name ? 'is-error' : ''}
                        placeholder="Jane Smith"
                        aria-invalid={!!errors.name}
                      />
                      {errors.name && <span className="cu-error">{errors.name}</span>}
                    </div>

                    <div className="cu-field">
                      <label htmlFor="company">Company</label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your organisation"
                      />
                    </div>
                  </div>

                  <div className="cu-field-row">
                    <div className="cu-field">
                      <label htmlFor="email">Email <span className="cu-req">*</span></label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        className={errors.email ? 'is-error' : ''}
                        placeholder="jane@company.co.uk"
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && <span className="cu-error">{errors.email}</span>}
                    </div>

                    <div className="cu-field">
                      <label htmlFor="phone">Phone</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div className="cu-field">
                    <label htmlFor="service">How can we help? <span className="cu-req">*</span></label>
                    <select
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className={errors.service ? 'is-error' : ''}
                      aria-invalid={!!errors.service}
                    >
                      <option value="">Select a service…</option>
                      {SERVICES.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                    {errors.service && <span className="cu-error">{errors.service}</span>}
                  </div>

                  <div className="cu-field">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us a little about your business and what you need…"
                    />
                  </div>

                  <div className="cu-field cu-consent">
                    <label className="cu-check">
                      <input
                        type="checkbox"
                        name="consent"
                        checked={form.consent}
                        onChange={handleChange}
                        aria-invalid={!!errors.consent}
                      />
                      <span>
                        I agree to Sustainability Advisory Ltd storing my details to respond to my
                        enquiry.
                      </span>
                    </label>
                    {errors.consent && <span className="cu-error">{errors.consent}</span>}
                  </div>

                  <button type="submit" className="btn btn-primary cu-submit">
                    Send message
                    <IconArrowRight width={18} height={18} />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default ContactUs;
